import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import ModalTemplate from "@/shared/components/ModalTemplate/ModalTemplate.vue";
import { Http } from "@status/codes";
import AddFieldGroup from "@/shared/components/AddFieldGroup/AddFieldGroup.vue";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import FormModalViewer from "@/shared/components/FormModalViewer/FormModalViewer.vue";
import { GroupFullSchema } from "@/modules/InfoGroup/models/GroupFullSchema";
import { ListService } from "@/modules/List/services/ListService";
import { PhoneSchema } from "@/shared/models/PhoneSchema";
import { ManagedListsService } from "@/shared/services/ManagedListsService";
import { MANAGED_LISTS_TYPES } from "@/shared/constants/GeneralConstants";
import { Icon } from "@/shared/models/Icon";
import VJsf from "@koumoul/vjsf/lib/VJsf.js";
import VuePhoneNumberInput from "vue-phone-number-input";
import { OrderService } from "@/modules/Orders/services/OrderService";
import {
  ORDER_ACCESS,
  ORDER_CONSTANTS
} from "@/modules/Orders/constants/OrderConstants";
import {
  FORM_MESSAGES,
  FORM_VALIDATORS,
  ICON_LIST_FIXED
} from "@/shared/constants/FormConstants";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { FieldOrder } from "@/modules/Orders/models/FieldOrder";
import { OrderTemplate } from "@/modules/Orders/models/OrderTemplate";
import { FullSchema } from "@/shared/models/FullSchema";
import { MedicalOrderService } from "@/modules/MedicalOrder/services/MedicalOrderService";
import { MedicalOrderPreviewEventTimelineModel } from "@/modules/MedicalOrder/models/MedicalOrderPreviewEventTimelineModel";
import { AxiosError } from "axios";
import { LIST_ACCESS } from "@/modules/List/constants/ListConstants";

@Component({
  name: "Order",
  components: {
    ModalTemplate,
    AddFieldGroup,
    FormModalViewer,
    VJsf,
    VuePhoneNumberInput
  }
})
export default class Order extends Vue {
  @Inject() orderService!: OrderService;
  @Inject() listService!: ListService;
  @Inject() managedListsService!: ManagedListsService;
  @Inject() medicalOrderService!: MedicalOrderService;

  @Prop() private id!: string;
  @Prop() private title!: string;

  TEXTS: { [key: string]: string } = {
    INT_NAME: ORDER_CONSTANTS.PREVIEW_EXAMPLE.INTERNAL_NAME.TEXT,
    EXT_NAME: ORDER_CONSTANTS.PREVIEW_EXAMPLE.EXTERNAL_NAME.TEXT,
    CAT_NAME: ORDER_CONSTANTS.PREVIEW_EXAMPLE.CATEGORY.TEXT,
    CUPS_NAME: ORDER_CONSTANTS.PREVIEW_EXAMPLE.CUPS.TEXT,
    PRICE_LEVEL: ORDER_CONSTANTS.LABELS.PRICE_LEVEL,
    STATE_LABEL: ORDER_CONSTANTS.LABELS.STATE,
    EXTERNAL_NUMBER: ORDER_CONSTANTS.LABELS.EXTERNAL_NUMBER,
    DIAG_CODE: ORDER_CONSTANTS.LABELS.DIAG_CODE,
    CANCEL_VALUE: ORDER_CONSTANTS.LABELS.CANCEL_VALUE,
    SERVICE_VALUE: ORDER_CONSTANTS.LABELS.SERVICE_VALUE,
    BILL_TYPE: ORDER_CONSTANTS.LABELS.BILL_TYPE,
    ORDER_TYPE: ORDER_CONSTANTS.LABELS.ORDER_TYPE,
    CAMPUS: ORDER_CONSTANTS.LABELS.CAMPUS,
    DESCRIPTION: ORDER_CONSTANTS.LABELS.DESCRIPTION,
    EVENT: ORDER_CONSTANTS.LABELS.EVENT
  };

  FIELDS_TITLE = ORDER_CONSTANTS.FIELDS_TITLE;
  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  requiredField = [FORM_VALIDATORS.REQUIRED_FIELD];
  clearModel = false;
  dialogPreview = false;
  fieldsOrder: FieldOrder[] = [];
  orderTemplateEditing!: OrderTemplate;
  orderSchema: FullSchema = {
    title: "",
    description: "",
    type: "object",
    properties: {},
    required: []
  };
  schema: GroupFullSchema = {
    allOf: [this.orderSchema],
    type: "object"
  };
  phoneSchemaCharged = false;
  phoneTypeList: PhoneSchema[] = [];
  iconUrl = "";
  helpUrl = "";
  helpTxt = "";
  orderTempRef = "";
  originalFields!: FieldOrder[];
  iconsList = ICON_LIST_FIXED;
  imagesList: string[] = [];
  visibleDashboardFields: string[] = [];
  editableDashboardFields: string[] = [];

  orderTemplateName = this.title;
  model: { [key: string]: string } = {};
  order = {
    state: "",
    payment: 0,
    orderType: "",
    billType: "",
    cancelValue: 0,
    diagnosticPrimaryCode: "",
    campus: "",
    description: "",
    priceLevel: "",
    event: "",
    service: {
      category: "",
      internalName: ""
    },
    cupsPriceLevel: {
      externalName: "",
      cups: ""
    }
  };

  async mounted() {
    Object.keys(this.TEXTS).forEach(key => {
      this.TEXTS[key] = this.TEXTS[key].replace("*", "");
    });
    const consult = [];

    consult.push(this.getTemplate());
    consult.push(this.getIconsImages());
    consult.push(this.getMedicalOrder());

    await Promise.all(consult);
    this.phoneSchemaCharged = true;
    this.dialogPreview = true;
  }

  async getMedicalOrder() {
    const response = await this.medicalOrderService.getMedicalOrderById(
      this.id
    );

    const {
      model,
      ...order
    } = response.data as MedicalOrderPreviewEventTimelineModel;

    this.order = order;
    this.model = model;
    this.clearModel = true;
  }

  async getIconsImages() {
    try {
      const iconResponse = await this.managedListsService.getIconsImages(
        MANAGED_LISTS_TYPES.ICONS
      );
      const imagesResponse = await this.managedListsService.getIconsImages(
        MANAGED_LISTS_TYPES.IMAGES
      );

      this.iconsList = (iconResponse.data as Icon[]) || ICON_LIST_FIXED;
      this.imagesList = (imagesResponse.data as string[]) || [];
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST + LIST_ACCESS.GET);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  closeModalPreview() {
    this.dialogPreview = false;
    this.$emit("closeModal");
  }

  async getTemplate() {
    try {
      const responseData = await this.orderService.getTemplate();

      if (responseData.code == Http.Found && responseData.data !== undefined) {
        this.helpTxt = responseData.data.uiSetup.helpText ?? "";
        this.helpUrl = responseData.data.uiSetup.helpUrl ?? "";
        this.iconUrl = responseData.data.uiSetup.iconUrl ?? "";
        this.fieldsOrder = responseData.data.fields;
        this.originalFields = [...responseData.data.fields];
        this.orderTempRef = responseData.data.key ?? "";
        this.orderTemplateEditing = {
          fields: responseData.data.fields,
          uiSetup: responseData.data.uiSetup
        };
        this.visibleDashboardFields =
          (responseData.data.dashboardVisibleFields as string[]) ?? [];
        this.editableDashboardFields =
          (responseData.data.dashboardEditableFields as string[]) ?? [];

        await this.getSchema();
      } else {
        Snackbar.popInfo(responseData.message);
      }
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST + ORDER_ACCESS.GET);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getSchema() {
    try {
      const responseData = await this.orderService.getOrderTempSchema();
      if (responseData.code === Http.Found && responseData.data !== undefined) {
        this.fieldsOrder.forEach(field => {
          field.schema.readOnly = true;
          field.schema["x-style"] = STYLE_CLASSES.FORMULA;
          if (field.type === "lista") {
            if (field.visualElement === "checkbox") {
              field.schema.items =
                responseData.data?.allOf[0].properties[field.key].items;
            } else {
              field.schema.oneOf =
                responseData.data?.allOf[0].properties[field.key].oneOf;
            }
          }
        });
      } else {
        Snackbar.popInfo(responseData.message);
      }
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST + ORDER_ACCESS.GET);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  @Watch("fieldsOrder")
  loadSchema() {
    this.orderSchema.required = [];
    this.orderSchema.properties = {};
    this.fieldsOrder.map(field => {
      this;
      Object.assign(this.orderSchema.properties, {
        [field.key]: field.schema
      });
      field.required ? this.orderSchema.required?.push(field.key) : null;
      const exist = this.originalFields.find(f => f.key === field.key);
      if (exist) field.default = true;
    });

    if (this.phoneSchemaCharged) {
      this.getPhoneSchema();
      this.phoneSchemaCharged = false;
    }
  }

  getPhoneSchema() {
    const result = this.fieldsOrder.filter(
      field => field.type == ORDER_CONSTANTS.PHONE_TYPE
    );

    result.forEach(element => {
      this.phoneTypeList.push({
        slot: `custom-${element.key}`,
        title: element.schema.title,
        model: "",
        countryCode: "",
        countryCallingCode: "",
        required: element.required,
        enabled: element.editable,
        formattedNumber: ""
      });
    });
  }
}
